import {
  IFarmInputProgram,
  IFdApplicationType,
  ISupportCustomer,
} from 'constants/interfaces';

type CustomerScope = {
  emails: Set<string>;
  farmerIds: Set<string>;
  farmIds: Set<string>;
  userIds: Set<string>;
};

const lshCleanScopeValue = (value?: string | number | null) =>
  value === undefined || value === null ? '' : String(value).trim();

const lshLowerScopeValue = (value?: string | null) =>
  lshCleanScopeValue(value).toLowerCase();

function lshBuildCustomerScope(customers: ISupportCustomer[]): CustomerScope {
  return customers.reduce<CustomerScope>(
    (scope, customer) => {
      if (customer.farmerAccepted === false) return scope;

      const farmer = customer.farmer;
      const farmerId = lshCleanScopeValue(farmer?.id);
      const userId = lshCleanScopeValue(farmer?.userId);
      const farmId = lshCleanScopeValue(farmer?.farm?.id);
      const email = lshLowerScopeValue(farmer?.email);

      if (farmerId) scope.farmerIds.add(farmerId);
      if (userId) scope.userIds.add(userId);
      if (farmId) scope.farmIds.add(farmId);
      if (email) scope.emails.add(email);

      return scope;
    },
    {
      emails: new Set<string>(),
      farmerIds: new Set<string>(),
      farmIds: new Set<string>(),
      userIds: new Set<string>(),
    },
  );
}

function lshHasScope(scope: CustomerScope) {
  return (
    scope.farmerIds.size > 0 ||
    scope.userIds.size > 0 ||
    scope.farmIds.size > 0 ||
    scope.emails.size > 0
  );
}

function lshApplicationMatchesScope(
  application: IFdApplicationType,
  scope: CustomerScope,
) {
  if (!lshHasScope(scope)) return false;

  const externalUserId = lshCleanScopeValue(
    application.applyingUser?.externalId,
  );
  const fdUserId = lshCleanScopeValue(
    application.applyingUser?.id ?? application.user?.id,
  );
  const fallbackExternalUserId = lshCleanScopeValue(
    application.user?.externalId,
  );
  const applicantEmail = lshLowerScopeValue(application.applyingUser?.email);
  const farmId = lshCleanScopeValue(application.farm?.id);

  return (
    (externalUserId && scope.userIds.has(externalUserId)) ||
    (externalUserId && scope.farmerIds.has(externalUserId)) ||
    (fdUserId && scope.userIds.has(fdUserId)) ||
    (fdUserId && scope.farmerIds.has(fdUserId)) ||
    (fallbackExternalUserId && scope.userIds.has(fallbackExternalUserId)) ||
    (fallbackExternalUserId && scope.farmerIds.has(fallbackExternalUserId)) ||
    (applicantEmail && scope.emails.has(applicantEmail)) ||
    (farmId && scope.farmIds.has(farmId))
  );
}

function lshProgramMatchesScope(
  program: IFarmInputProgram,
  scope: CustomerScope,
) {
  if (!lshHasScope(scope)) return false;

  const farmerId = lshCleanScopeValue(program.farmerId ?? program.farmer?.id);
  const farmerUserId = lshCleanScopeValue(program.farmer?.userId);
  const farmerEmail = lshLowerScopeValue(program.farmer?.email);
  const linkedApplication = (program as any).fundingApplication;
  const linkedApplicationUserId = lshCleanScopeValue(
    linkedApplication?.applyingUser?.externalId ?? linkedApplication?.user?.id,
  );

  return (
    (farmerId && scope.farmerIds.has(farmerId)) ||
    (farmerUserId && scope.userIds.has(farmerUserId)) ||
    (farmerEmail && scope.emails.has(farmerEmail)) ||
    (linkedApplicationUserId && scope.userIds.has(linkedApplicationUserId))
  );
}

export function lshFilterFdApplicationsForLinkedCustomers(
  applications: IFdApplicationType[],
  customers: ISupportCustomer[],
) {
  const scope = lshBuildCustomerScope(customers);
  return applications.filter((application) =>
    lshApplicationMatchesScope(application, scope),
  );
}

export function lshFilterInputProgramsForLinkedCustomers(
  programs: IFarmInputProgram[],
  customers: ISupportCustomer[],
) {
  const scope = lshBuildCustomerScope(customers);
  return programs.filter((program) => lshProgramMatchesScope(program, scope));
}
